import { gql }                     from 'apollo-boost';
import { maybeStaticVersionField } from "@src/util";


export const fragments = {

    ACTIVITY: {
        QUERY_RETURN:    `id name displayName description`,
        MUTATION_RETURN: `id name displayName description`,
        EDIT_FIELDS:     `
			id
			name
			description
			displayName
			refIdCode

			budgetValueConditions{
				id
				targetIntakeQuestionId
				conditionType
				answerValue
				budgetValue
			}
			defaultBudgetValue

			defaultTimeSpanUnit
			defaultTimeSpanValue
			projectTimingMilestone
			projectTimingCompletionMilestone
			projectTimingMilestoneOffsetMonths
			timeSpanConditions {
				id
				targetIntakeQuestionId
				conditionType
				answerValue
				timeSpanUnit
				timeSpanValue
			}

			defaultRecurrenceType
			defaultRecurrenceValue
			defaultRecurrenceUnit
			defaultRecurrenceMilestones{
				recurrenceMilestone
			}

			duplicationQuestions{ id name inputType dataValueSetId }
			childActivities { childActivityId parentCompletionRequired parentSchedulingEnforced }
			parentActivities { parentActivityId parentCompletionRequired parentSchedulingEnforced }
			triggeringSubObjectives{ id }

			subCategoryId

            businessFunctionId
			defaultOperationalCost
			defaultOngoingCostMin
			defaultOngoingCostMax
			defaultOngoingCostDurationQuarters
			professionalFees{ professionalFeeId }
			hires{
				headcount
				hireTypeId
				isHiringPoint
			}

			defaultCriticality

			tags{ tagName }
			isRequired
			perceivedAsContinuous
		`
    },

    PROJECT_ACTIVITY: {
        QUERY_RETURN:                   `
			id
			startTimeUtc
			endTimeUtc
			activity{ id displayName }
		`,
        MUTATION_RETURN:                `
			id
			startTimeUtc
			endTimeUtc
			activity{ id displayName }
		`,
        USER_EDIT_FIELDS_VITAL:         `
			id
			notes
			completionState
			activity { id isRequired }
			recurrenceMilestonesOverride {
				projectActivityId
				recurrenceMilestone
			}
			recurrenceCustomMilestones{
				projectActivityId
				projectCustomTimingMilestoneId
			}
			ongoingCostOverride
			ongoingCostOverrideNote
			operationalCostOverride
			operationalCostOverrideNote
			perceivedAsContinuousOverride
			recurrenceTypeOverride
			recurrenceUnitOverride
			recurrenceValueOverride
			startTimeOverrideUtc
            endTimeOverrideUtc
            defaultTimeSpanUnitOverride
            defaultTimeSpanValueOverride
            projectTimingMilestoneOffsetMonthsOverride
            projectTimingMilestoneOverride
			hiresOverride{
			    hireTypeId
			    headcount
			}
		`,
        USER_EDIT_FIELDS:               `
			id
			notes
			completionState
			recurrenceMilestonesOverride {
				projectActivityId
				recurrenceMilestone
			}
			recurrenceCustomMilestones{
				projectActivityId
				projectCustomTimingMilestoneId
			}
			ongoingCostOverride
			ongoingCostOverrideNote
			operationalCostOverride
			operationalCostOverrideNote
			perceivedAsContinuousOverride
			recurrenceTypeOverride
			recurrenceUnitOverride
			recurrenceValueOverride

			startTimeUtc
			startTimeOverrideUtc
			startTimeCalculatedUtc

			endTimeUtc
			endTimeOverrideUtc
			endTimeCalculatedUtc
			
			defaultTimeSpanValueOverride
            defaultTimeSpanUnitOverride
            projectTimingMilestoneOverride
            projectCustomTimingMilestoneId
            projectTimingMilestoneOffsetMonthsOverride

			professionalFees{ professionalFeeId professionalFee{ id name amount } },

			recurrenceValue
			recurrenceUnit
			recurrenceMilestones {
				projectActivityId
				recurrenceMilestone
			}
			recurrenceType

			hiresOverride{
			    hireTypeId
			    headcount
			}

			activity{
				id
				isRequired

				defaultTimeSpanUnit
				defaultTimeSpanValue
				description
				displayName
				name
				categoryId
				subCategoryId
				tags{ tagName }
				projectTimingMilestone
				businessFunctionId
				hires{
					headcount
					hireTypeId
					isHiringPoint
				},
				defaultCriticality
				defaultOperationalCost
				defaultOngoingCostMin
				defaultOngoingCostMax
				defaultOngoingCostDurationQuarters
				perceivedAsContinuous
			}
			perceivedAsContinuous
		`,
        ADD_WATCHER_MUTATION_RETURN:    `
			account{ firstName, lastName }
		`,
        REMOVE_WATCHER_MUTATION_RETURN: `
			account{ firstName, lastName }
		`,
    },

    STATIC_PROJECT_ACTIVITY:              {
        QUERY_RETURN:           ( projectVersionId = null ) => `
			id
			startDate
			endDate
			name
			activity{ id }
		`,
        MUTATION_RETURN:        ( projectVersionId = null ) => `
			id
			startDate
			endDate
			name
			activity{ id }
		`,
        USER_EDIT_FIELDS_VITAL: ( projectVersionId?: string ) => `
			id
			notes
			completionState
			activity { id }
			ongoingCostPerQuarter
			ongoingCostNote
			operationalCost
			operationalCostNote
			
		    scheduledBudgetItems{
                id
                name
                amount
                note
            }
			perceivedAsContinuous
			startTimeOverrideUtc
            endTimeOverrideUtc
            defaultTimeSpanUnitOverride
            defaultTimeSpanValueOverride
			
			requiredHires{
			    hireTypeId
			    headcount
			}
		`,
        USER_EDIT_FIELDS:       ( projectVersionId?: string ) => `
			id
			name
            description
            businessFunctionId
            
            criticality
            percentDone
            isAtRisk
            
            startDate
            endDate
            constraintDate
            constraintType
            deadlineDate
            manuallyScheduled
            
            operationalCost
            operationalCostNote
            operationalCostReplacesProfessionalFees
           
            scheduledBudgetItems {
                id
                name
                amount
                note
                quarter
                year
            }
               
            ongoingCostNote
            ongoingCostPerQuarter
            ongoingCostQuarters
            
            requiredHires {
               headcount
               hireTypeId
            }
		`,
        GANTT_QUERY_FIELDS:     ( projectVersionId?: string ) => `
            id
            name
            description
            businessFunctionId
            businessFunction { id name description colorValue iconName isArchived resourceCode }
            notes
            corvalType
            
            startDate
            endDate
            deadlineDate
            duration
            constraintDate
            constraintType
            completionState
            manuallyScheduled
            
            perceivedAsContinuous
            
            isAtRisk
            criticality
            percentDone
            
            categoryId
            categoryName
            categoryDescription
            categoryOrdinal
            
            subCategoryId
            subCategoryName
            subCategoryDescription
            subCategoryOrdinal
            
            subObjectiveId
            subObjectiveName
            subObjectiveDescription
            subObjectiveOrdinal
            
            operationalCost
            operationalCostNote
            operationalCostReplacesProfessionalFees
            
            ongoingCostPerQuarter
            ongoingCostQuarters
            ongoingCostNote
            
            scheduledBudgetItems {
                id
                name
                amount
                note
                quarter
                year
            }
            
            requiredHires {
                headcount
                hireTypeId
                hireType { 
                    id 
                    name 
                    businessFunctionId
                    businessFunction { id name }
                }
            }
        `,
    },
    STATIC_PROJECT_ACTIVITY_DEPENDENCIES: {
        QUERY_RETURN:       ( projectVersionId?: string ) => `id from to`,
        GANTT_QUERY_FIELDS: ( projectVersionId?: string ) => `
            id
            from
            to
            type
            lag
            lagUnit
            active
        `,
    },
    STATIC_PROJECT_MILESTONE_TASKS:       {
        GANTT_QUERY_FIELDS: ( projectVersionId?: string ) => `
            id
            name
            description
            startDate
            milestoneType
        `,
    },
}

export const queries = {

    GET_PROJECT_ACTIVITIES: ( returnQueryString = fragments.PROJECT_ACTIVITY.QUERY_RETURN, projectVersionId = null ) => {

        const query = `
            query GetProjectActivities(
                ${ projectVersionId ? '$projectVersionId: Uuid!' : '$projectId: Uuid!' },
                $startTime: DateTime,
                $endTime:   DateTime,
                $order:     [${ maybeStaticVersionField( 'ProjectActivitySortInput!', projectVersionId, true ) }]
                $where:     ${ maybeStaticVersionField( 'ProjectActivityFilterInput', projectVersionId, true ) }
                $hireTypeIdIn: [Uuid!],
                $hiresSatisfied: Boolean,
                $includeGeneratedRecurrences: Boolean,
            ){
    
                ${ maybeStaticVersionField( 'projectActivites', projectVersionId ) }(
                    order:          $order,
                    where:          $where,
                    ${ projectVersionId ? `projectVersionId: $projectVersionId` : `projectId: $projectId` },
                    startTimeUtc:   $startTime,
                    endTimeUtc:     $endTime,
                    hireTypeIdIn:   $hireTypeIdIn,
                    hiresSatisfied: $hiresSatisfied,
                    includeGeneratedRecurrences: $includeGeneratedRecurrences,
                ) {
    
                    ${ returnQueryString }
                }
            }`

        return gql( query );
    },

    GET_PROJECT_ACTIVITY_TIMING_LEADS: ( returnQueryString = fragments.PROJECT_ACTIVITY.QUERY_RETURN ) => gql`

        query GetProjectActivityTimingLeads(
            $projectId: Uuid!,
            $projectSubObjectiveIds: [Uuid!]!
        ){

            projectActivityTimingLeads(
                projectId:              $projectId,
                projectSubObjectiveIds: $projectSubObjectiveIds,
            ) {

                ${ returnQueryString }
            }
        }`,

    GET_ACTIVITIES: ( returnQueryString = fragments.ACTIVITY.QUERY_RETURN ) => gql`
        query GetActivities( $order: [ActivitySortInput!], $where: ActivityFilterInput ){
            activities( order: $order, where: $where) {
                ${ returnQueryString }
            }
        }`,

    GET_ACTIVITY: ( returnQueryString = fragments.ACTIVITY.QUERY_RETURN ) => gql`
        query GetActivity( $id: Uuid! ){
            activities( where: { id: { eq: $id } } ) {
                ${ returnQueryString }
            }
        }`,

    CHECK_ACTIVITY_DEPENDENCY_LOOP_DETECTION: ( returnQueryString = fragments.ACTIVITY.QUERY_RETURN ) => gql`
        query GetActivityLoopDetection( $parentActivityIds:[Uuid!]!, $childActivityIds:[Uuid!]!, $tracePaths:Boolean!  ){
            activityRelationshipLoops(
                parentActivityIds: $parentActivityIds,
                childActivityIds: $childActivityIds,
                tracePaths: $tracePaths,
            ) {
                loopsDetected
                paths {
                    activityId
                    step
                }
            }
        }`,
}

export const mutations = {

    CREATE_ACTIVITY: ( returnQueryString = fragments.ACTIVITY.MUTATION_RETURN ) => gql`
        mutation CreateActivity( $input: ActivityCreateCommandInput! ){
            createActivity( input: $input ) {
                ${ returnQueryString }
            }
        }`,

    UPDATE_ACTIVITY: ( returnQueryString = fragments.ACTIVITY.MUTATION_RETURN ) => gql`
        mutation UpdateActivity( $input: ActivityUpdateCommandInput! ){
            updateActivity( input: $input ) {
                ${ returnQueryString }
            }
        }`,

    DELETE_ACTIVITY: () => gql`
        mutation DeleteActivity( $input: ActivityDeleteCommandInput! ){
            deleteActivity( input: $input )
        }`,

    UPDATE_PROJECT_ACTIVITIES: ( returnQueryString = fragments.PROJECT_ACTIVITY.MUTATION_RETURN ) => {

        const query = `
        mutation UpdateProjectActivities( $input: ProjectActivityUpdateCommandInput! ){
            updateProjectActivities( input: $input )  {
                ${ returnQueryString }
            }
        }`

        return gql( query );
    },

    UPDATE_PROJECT_ACTIVITIES_WITH_CHANGESET: ( returnQueryString = fragments.PROJECT_ACTIVITY.MUTATION_RETURN ) => gql`
        mutation UpdateProjectActivitiesWithChangeSet( $input: ProjectActivityUpdateCommandInput! ){
            updateProjectActivitiesWithChangeset( input: $input )  {
                ${ returnQueryString }
            }
        }`,

    PROPAGATE_PROJECT_ACTIVITY_CHANGES: ( returnQueryString = fragments.PROJECT_ACTIVITY.MUTATION_RETURN ) => gql`
        mutation PropagateProjectActivityUpdates( $input: ProjectActivityPropagateCommandInput! ){
            propagateProjectActivityValues( input: $input )  {
                ${ returnQueryString }
            }
        }`,

    ADD_WATCHER_TO_PROJECT_ACTIVITY: ( returnQueryString = fragments.PROJECT_ACTIVITY.ADD_WATCHER_MUTATION_RETURN ) => gql`
        mutation AddWatcherToProjectActivity( $input:ProjectActivityAccountCreateCommandInput! ){
            createProjectActivityAccount( input: $input )  {
                ${ returnQueryString }
            }
        }`,

    REMOVE_WATCHER_FROM_PROJECT_ACTIVITY: ( returnQueryString = fragments.PROJECT_ACTIVITY.REMOVE_WATCHER_MUTATION_RETURN ) => gql`
        mutation RemoveWatcherFromProjectActivity( $input:ProjectActivityAccountDeleteCommandInput! ){
            deleteProjectActivityAccount( input: $input )  {
                ${ returnQueryString }
            }
        }`,
}

export const functions = {
    PROJECT_ACTIVITIES_FIELD: ( useStaticVersion = false ) => {
        return maybeStaticVersionField( 'projectActivites', useStaticVersion )
    }
}

export const ActivityQueries = {
    fragments,
    queries,
    mutations,
    functions,
}
